import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/**
 * The PWA slice's whole HTTP surface — push **registration**, and nothing else
 * (`docs/standards/code-organization.md`).
 *
 * ## Two endpoints, and what is deliberately not here
 *
 * Registration is storing a browser's push subscription against the member's account so that
 * something can later send to it. **Sending is not here and must not arrive here.** Delivery is
 * the comms domain's — the catalogue already lists `push` among its channels and `send` answers
 * `channel-not-built` for it — and a screen that could send a push would be a screen that
 * decides who gets notified about what, which is `MEM-041`'s and `MSG`'s.
 *
 * ## Why the application server key is fetched rather than built in
 *
 * `pushManager.subscribe` needs the VAPID public half. It is a deployment fact, not a build fact:
 * the key pair lives with whatever will eventually send, and burning the public half into the
 * bundle would mean a key rotation is a front-end release. So it is asked for, and a deployment
 * with no key configured answers `configured: false` — at which point the app simply never offers
 * push. That is `MEM-040` AC-5 arriving for free: with no key there is no offer, no prompt, and
 * every badge, notice and screen is exactly as it was.
 *
 * ## Nothing here is retried, and nothing here is queued
 *
 * `MEM-043` AC-4. A registration that cannot reach the service fails, the member is told, and no
 * subscription is written anywhere locally to be replayed later. A stale replayed subscription
 * would be a notification sent to a browser that has since unsubscribed, which is the harmless
 * end of the same mistake that queued check-ins are the harmful end of.
 */

/** The application server key, or an honest statement that this deployment has none. */
export interface PushConfiguration {
  readonly configured: boolean;
  /** Base64url VAPID public key. `null` whenever `configured` is false. */
  readonly publicKey: string | null;
}

/**
 * A browser's push subscription, in the shape `PushSubscription.toJSON()` produces.
 *
 * **It is a credential.** `endpoint` is a capability URL: anyone holding it can push to this
 * browser, and `keys.auth` is the secret that authenticates the payload. It is sent in a request
 * body and never in a query string, it is never logged, and the API's own README carries the
 * same warning from the other side.
 */
export interface PushSubscriptionPayload {
  readonly endpoint: string;
  readonly expirationTime: number | null;
  readonly keys: { readonly p256dh: string; readonly auth: string };
}

export const PUSH_CONFIGURATION_QUERY_KEY = ['push-subscriptions', 'key'] as const;

/**
 * Asks whether this deployment can do push at all.
 *
 * `enabled` is the caller's, and every caller passes "is there a member". A signed-out visitor
 * has no account to register a subscription against, so asking would be a 401 on a screen that
 * has no push offer on it anyway.
 */
export function usePushConfiguration(options: {
  readonly enabled: boolean;
}): UseQueryResult<PushConfiguration> {
  return useQuery({
    queryKey: PUSH_CONFIGURATION_QUERY_KEY,
    queryFn: () => apiRequest<PushConfiguration>('/api/push-subscriptions/key'),
    enabled: options.enabled,
    // The key changes on a rotation, which is rare and never mid-session.
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

/** Stores this browser's subscription against the signed-in member. Idempotent on the endpoint. */
export function useRegisterPushSubscription(): ReturnType<
  typeof useMutation<void, Error, PushSubscriptionPayload>
> {
  return useMutation<void, Error, PushSubscriptionPayload>({
    mutationFn: (subscription) =>
      apiRequest<void>('/api/push-subscriptions', { method: 'PUT', body: subscription }),
    retry: false,
  });
}

/**
 * Forgets a subscription.
 *
 * `POST …/forget` with the endpoint in the **body** rather than `DELETE …?endpoint=…`: the
 * endpoint is a credential, and a credential in a query string is a credential in an access log.
 */
export function useForgetPushSubscription(): ReturnType<
  typeof useMutation<void, Error, { readonly endpoint: string }>
> {
  return useMutation<void, Error, { readonly endpoint: string }>({
    mutationFn: (body) =>
      apiRequest<void>('/api/push-subscriptions/forget', { method: 'POST', body }),
    retry: false,
  });
}
